import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Stack, Box, Typography, Chip, Button, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Alert } from "@mui/material";
import { format } from "date-fns";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
// Local types for Cabin, TimeSlot, SelectedSlot
interface Cabin {
    id: string;
    name: string;
    color?: string;
}
interface TimeSlot {
    time: string;
    display: string;
}
interface SelectedSlot {
    date: Date;
    time: string;
}


interface CabinSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    dialogSelectedSlots: SelectedSlot[];
    setDialogSelectedSlots: (slots: SelectedSlot[]) => void;
    timeSlots: TimeSlot[];
    getSlotStatus: (date: Date, timeSlot: string) => { booked: number; total: number; available: number };
    isDialogSlotSelected: (date: Date, timeSlot: string) => boolean;
    handleDialogSlotToggle: (date: Date, timeSlot: string) => void;
    getAvailableCabinsForDialogSlots: () => Cabin[];
    handleCabinBook: (cabinId: string) => void;
    getBookedCabins: (date: Date, timeSlot: string) => Cabin[];
    getBookingForSlot: (date: Date, timeSlot: string, cabinId: string) => any;
}

const CabinSelectionDialog: React.FC<CabinSelectionDialogProps> = ({
    open,
    onClose,
    dialogSelectedSlots,
    setDialogSelectedSlots,
    timeSlots,
    getSlotStatus,
    isDialogSlotSelected,
    handleDialogSlotToggle,
    getAvailableCabinsForDialogSlots,
    handleCabinBook,
    getBookedCabins,
    getBookingForSlot
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Book Cabin - Select Time Slots
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Select one or more time slots, then choose an available cabin
                    </Typography>
                </Box>
            </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
            {dialogSelectedSlots.length > 0 && (
                <Stack spacing={3}>
                    {/* Time Slot Selection */}
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Select Time Slots
                        </Typography>
                        {/* Currently Selected Slots */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Selected Slots ({dialogSelectedSlots.length}):
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {dialogSelectedSlots.map((slot, index) => (
                                    <Chip
                                        key={index}
                                        label={`${format(slot.date, "EEE, MMM dd")} at ${timeSlots.find((s) => s.time === slot.time)?.display}`}
                                        onDelete={dialogSelectedSlots.length > 1 ? () => {
                                            setDialogSelectedSlots(dialogSelectedSlots.filter((_, i) => i !== index))
                                        } : undefined}
                                        color="primary"
                                        size="small"
                                    />
                                ))}
                            </Box>
                        </Box>
                        {/* Additional Time Slots */}
                        <Typography variant="subtitle2" gutterBottom>
                            Add More Time Slots for Today:
                        </Typography>
                        <Box sx={{ maxHeight: 300, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 2, p: 2 }}>
                            {(() => {
                                const today = new Date()
                                return (
                                    <Box key={format(today, "yyyy-MM-dd")} sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                            {format(today, "EEEE, MMM dd, yyyy")}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                            {timeSlots.map((slot) => {
                                                const isSelected = isDialogSlotSelected(today, slot.time)
                                                const status = getSlotStatus(today, slot.time)
                                                const isFullyBooked = status.available === 0
                                                return (
                                                    <Button
                                                        key={slot.time}
                                                        variant={isSelected ? "contained" : "outlined"}
                                                        size="small"
                                                        disabled={isFullyBooked}
                                                        onClick={() => handleDialogSlotToggle(today, slot.time)}
                                                        sx={{
                                                            minWidth: "80px",
                                                            bgcolor: isSelected ? "secondary.main" : "transparent",
                                                            color: isSelected ? "secondary.contrastText" : isFullyBooked ? "text.disabled" : "text.primary",
                                                            "&:hover": {
                                                                bgcolor: isSelected ? "secondary.dark" : !isFullyBooked ? "secondary.light" : undefined,
                                                            },
                                                        }}
                                                    >
                                                        {slot.display}
                                                        {!isFullyBooked && (
                                                            <Typography variant="caption" sx={{ ml: 1, fontSize: "0.7rem" }}>
                                                                ({status.available})
                                                            </Typography>
                                                        )}
                                                    </Button>
                                                )
                                            })}
                                        </Box>
                                    </Box>
                                )
                            })()}
                        </Box>
                    </Box>
                    <Divider />
                    {/* Available Cabins */}
                    {getAvailableCabinsForDialogSlots().length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "success.main", mb: 1 }}>
                                Available Cabins for All Selected Slots ({getAvailableCabinsForDialogSlots().length})
                            </Typography>
                            <List sx={{ py: 0, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                                {getAvailableCabinsForDialogSlots().map((cabin: any) => (
                                    <ListItem key={cabin.id} disablePadding>
                                        <ListItemButton
                                            onClick={() => handleCabinBook(cabin.id)}
                                            sx={{
                                                "&:hover": { bgcolor: "success.light", color: "white" },
                                                transition: "all 0.2s",
                                            }}
                                        >
                                            <ListItemIcon>
                                                <CheckCircleIcon sx={{ color: "success.main" }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={cabin.name}
                                                secondary={`Book this cabin for ${dialogSelectedSlots.length} time slot${dialogSelectedSlots.length !== 1 ? "s" : ""}`}
                                            />
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    borderRadius: "50%",
                                                    bgcolor: cabin.color,
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                    {/* No cabins available for all slots */}
                    {getAvailableCabinsForDialogSlots().length === 0 && (
                        <Box sx={{ textAlign: "center", py: 3 }}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                No cabins are available for all selected time slots
                            </Alert>
                            <Typography variant="body2" color="text.secondary">
                                Try selecting fewer time slots or different times
                            </Typography>
                        </Box>
                    )}
                    {/* Show individual slot availability for reference */}
                    {dialogSelectedSlots.length === 1 && (
                        <Box>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" gutterBottom>
                                Booked Cabins for {format(dialogSelectedSlots[0].date, "EEE, MMM dd")} at {timeSlots.find((s) => s.time === dialogSelectedSlots[0].time)?.display}
                            </Typography>
                            {getBookedCabins(dialogSelectedSlots[0].date, dialogSelectedSlots[0].time).length > 0 ? (
                                <List sx={{ py: 0, bgcolor: "#fafafa", borderRadius: 2 }}>
                                    {getBookedCabins(dialogSelectedSlots[0].date, dialogSelectedSlots[0].time).map((cabin) => {
                                        const booking = getBookingForSlot(dialogSelectedSlots[0].date, dialogSelectedSlots[0].time, cabin.id)
                                        return (
                                            <ListItem key={cabin.id} sx={{ opacity: 0.7 }}>
                                                <ListItemIcon>
                                                    <CancelIcon sx={{ color: "error.main" }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={cabin.name}
                                                    secondary={
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <PersonIcon sx={{ fontSize: 14 }} />
                                                            <Typography variant="caption">Booked by {booking?.bookedBy}</Typography>
                                                        </Stack>
                                                    }
                                                />
                                                <Box
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: "50%",
                                                        bgcolor: cabin.color,
                                                        opacity: 0.5,
                                                    }}
                                                />
                                            </ListItem>
                                        )
                                    })}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                    No other cabins are booked for this time slot
                                </Typography>
                            )}
                        </Box>
                    )}
                </Stack>
            )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={onClose}>Close</Button>
        </DialogActions>
    </Dialog>
);

export default CabinSelectionDialog;
